import { useTranslation } from 'react-i18next';
import { Copy } from 'lucide-react';
import type {
  ClinicalPrescriptionPd,
  EligibleExaminer,
  EyeExaminationDetail,
} from '@optiflow/types';
import { Button } from '@/components/ui/Button';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useLocale } from '@/hooks/useLocale';
import { formatDateTime } from '@/utils/format';
import { eyeHasValues, fieldPath, type ExaminationForm, type FormErrors } from '../examinationForm';
import { ClinicalEyeGrid } from './ClinicalEyeGrid';
import { ClinicalTextArea } from './ClinicalTextArea';
import { PdMeasurementPicker, PdSnapshot } from './PdMeasurementPicker';

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="field-label">{label}</p>
      <div className="text-ink text-sm">{children}</div>
    </div>
  );
}

interface VisitSectionProps {
  form: ExaminationForm;
  update?: (next: ExaminationForm) => void;
  customerLabel: string;
  branchName: string | null;
  examiners: EligibleExaminer[] | undefined;
  examinerName: string | null;
  examinedAtIso: string | null;
  errors: FormErrors;
}

/** Who, where and when. The customer and branch come from the record or the request, never from typing. */
export function VisitSection({
  form,
  update,
  customerLabel,
  branchName,
  examiners,
  examinerName,
  examinedAtIso,
  errors,
}: VisitSectionProps) {
  const { t } = useTranslation();
  const { locale } = useLocale();

  const options = [
    { value: '', label: t('medical.visit.examinerNone') },
    ...(examiners ?? []).map((examiner) => ({ value: examiner.id, label: examiner.name })),
  ];
  // Someone named before who is no longer listed stays visible, so a save never drops them unseen.
  if (form.examinerId && !options.some((option) => option.value === form.examinerId)) {
    options.push({
      value: form.examinerId,
      label: t('medical.visit.examinerNotEligible', { name: examinerName ?? form.examinerId }),
    });
  }
  const examinerError = errors['visit.examinerId'];
  const dateError = errors['visit.examinedAt'];

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Fact label={t('medical.visit.customer')}>{customerLabel}</Fact>
      <Fact label={t('medical.visit.branch')}>{branchName ?? '—'}</Fact>

      {update ? (
        <Input
          type="datetime-local"
          dir="ltr"
          label={t('medical.visit.examinedAt')}
          value={form.examinedAt}
          error={dateError ? t(dateError.key, dateError.values) : undefined}
          onChange={(event) => update({ ...form, examinedAt: event.target.value })}
        />
      ) : (
        <Fact label={t('medical.visit.examinedAt')}>
          {examinedAtIso ? formatDateTime(examinedAtIso, locale) : '—'}
        </Fact>
      )}

      {update ? (
        <FormField
          label={t('medical.visit.examiner')}
          hint={t('medical.visit.examinerHint')}
          error={examinerError ? t(examinerError.key, examinerError.values) : undefined}
        >
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              hasError={Boolean(examinerError)}
              options={options}
              value={form.examinerId}
              onChange={(event) => update({ ...form, examinerId: event.target.value })}
            />
          )}
        </FormField>
      ) : (
        <Fact label={t('medical.visit.examiner')}>
          {examinerName ?? t('medical.history.examinerUnassigned')}
        </Fact>
      )}

      <div className="md:col-span-2">
        <ClinicalTextArea
          label={t('medical.visit.reasonForVisit')}
          value={form.reasonForVisit}
          maxLength={500}
          rows={2}
          onChange={update ? (reasonForVisit) => update({ ...form, reasonForVisit }) : undefined}
        />
      </div>
    </div>
  );
}

interface PrescriptionSectionProps {
  customerId: string;
  form: ExaminationForm;
  update?: (next: ExaminationForm) => void;
  onCopyFromSubjective?: () => void;
  detail: EyeExaminationDetail | undefined;
  canViewMeasurements: boolean;
  errors: FormErrors;
  /** An amendment's original prescription PD, shown as history beside the choice of a current one. */
  sourcePd?: ClinicalPrescriptionPd | null;
}

/**
 * The PD and the final prescription.
 *
 * Optional: the examiner decides whether this examination issues one. The PD is chosen from the customer's own measurements, never entered here. The number and issue date come only from the server, when the examination is completed; an issued prescription shows the PD frozen onto it, not the measurement as it is today.
 */
export function PrescriptionSection({
  customerId,
  form,
  update,
  onCopyFromSubjective,
  detail,
  canViewMeasurements,
  errors,
  sourcePd,
}: PrescriptionSectionProps) {
  const { t } = useTranslation();
  const issued = detail?.prescription ?? null;
  const expiryError = errors['prescription.expiresOn'];
  const pdError = errors['prescription.pdMeasurementId'];

  if (!update) {
    if (!issued) return <p className="text-muted text-sm">{t('medical.prescription.none')}</p>;
    return (
      <div className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-3">
          <Fact label={t('medical.prescription.number')}>
            {issued.prescriptionNumber ? (
              <span dir="ltr" className="font-mono">
                {issued.prescriptionNumber}
              </span>
            ) : (
              t('medical.prescription.notIssuedYet')
            )}
          </Fact>
          <Fact label={t('medical.prescription.issuedOn')}>
            <span dir="ltr" className="font-mono">
              {issued.issuedOn ?? '—'}
            </span>
          </Fact>
          <Fact label={t('medical.prescription.expiresOn')}>
            {issued.expiresOn ? (
              <span dir="ltr" className="font-mono">
                {issued.expiresOn}
              </span>
            ) : (
              t('medical.prescription.noExpiry')
            )}
          </Fact>
        </div>
        <ClinicalEyeGrid
          caption={t('medical.prescription.grid')}
          value={{ right: form.prescription.right, left: form.prescription.left }}
        />
        <Fact label={t('medical.pd.title')}>
          <PdSnapshot pd={issued.pd} frozen={issued.issuedOn !== null} />
        </Fact>
        <ClinicalTextArea
          label={t('medical.prescription.notes')}
          value={form.prescription.notes}
          maxLength={1000}
        />
      </div>
    );
  }

  const subjective = form.refractions.SUBJECTIVE;
  const hasSubjective = eyeHasValues(subjective.right) || eyeHasValues(subjective.left);

  return (
    <div className="space-y-5">
      <label className="text-ink flex items-center gap-2 text-sm font-medium">
        <input
          type="checkbox"
          checked={form.issuePrescription}
          onChange={(event) => update({ ...form, issuePrescription: event.target.checked })}
        />
        {t('medical.prescription.issue')}
      </label>

      {!form.issuePrescription ? (
        <p className="text-muted text-sm">{t('medical.prescription.notIssued')}</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Fact label={t('medical.prescription.number')}>
              {t('medical.prescription.notIssuedYet')}
            </Fact>
            <div className="text-end">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={!hasSubjective}
                onClick={onCopyFromSubjective}
              >
                <Copy className="size-4" aria-hidden />
                {t('medical.prescription.copyFromSubjective')}
              </Button>
              <p className="text-muted mt-1 text-xs">{t('medical.prescription.copyHint')}</p>
            </div>
          </div>

          <ClinicalEyeGrid
            caption={t('medical.prescription.grid')}
            value={{ right: form.prescription.right, left: form.prescription.left }}
            onChange={(eyes) =>
              update({ ...form, prescription: { ...form.prescription, ...eyes } })
            }
            errors={errors}
            errorPath={fieldPath.prescription}
          />

          {sourcePd && (sourcePd.totalPdMm || sourcePd.rightPdMm || sourcePd.leftPdMm) ? (
            <div className="border-line rounded-lg border border-dashed p-3">
              <p className="field-label">{t('medical.lifecycle.originalPd')}</p>
              <PdSnapshot pd={sourcePd} frozen />
              <p className="text-muted mt-1 text-xs">{t('medical.lifecycle.originalPdHint')}</p>
            </div>
          ) : null}

          <PdMeasurementPicker
            customerId={customerId}
            value={form.prescription.pdMeasurementId}
            canViewMeasurements={canViewMeasurements}
            error={pdError ? t(pdError.key, pdError.values) : undefined}
            onChange={(pdMeasurementId) =>
              update({ ...form, prescription: { ...form.prescription, pdMeasurementId } })
            }
          />

          <div className="grid gap-4 md:grid-cols-2">
            <Input
              type="date"
              dir="ltr"
              label={t('medical.prescription.expiresOn')}
              hint={t('medical.prescription.expiresOnHint')}
              value={form.prescription.expiresOn}
              error={expiryError ? t(expiryError.key, expiryError.values) : undefined}
              onChange={(event) =>
                update({
                  ...form,
                  prescription: { ...form.prescription, expiresOn: event.target.value },
                })
              }
            />
            <ClinicalTextArea
              label={t('medical.prescription.notes')}
              value={form.prescription.notes}
              maxLength={1000}
              rows={2}
              onChange={(notes) =>
                update({ ...form, prescription: { ...form.prescription, notes } })
              }
            />
          </div>
        </>
      )}
    </div>
  );
}
